import type { AttackKind, AttackPhase } from './simulation'
import type { ImpactCueKind } from './store'

export type RealityPhase='neutral'|'pressure'|'climax'|'collapse'
export type RealityActorSample = {
  health:number
  maxHealth:number
  attackFlash:number
  comboCount:number
  attackPhase:AttackPhase
  lastAttack:AttackKind|null
  y:number
}
export type RealityFrame = {
  intensity:number
  danger:number
  cinematic:number
  exposure:number
  fogNearScale:number
  fogFarScale:number
  particleEnergy:number
  chromaImpulse:number
  vignetteImpulse:number
  timeCompression:number
  worldResponse:number
  materialStress:number
  phase:RealityPhase
}
const clamp01=(v:number)=>Math.max(0,Math.min(1,v))
export function impactWeight(kind:ImpactCueKind|undefined,attack:AttackKind|null|undefined){
  const base=kind==='guard-break'?1:kind==='parry'?.88:kind==='throw'?.78:kind==='hit'?.68:kind==='block'?.42:0
  const attackBoost=attack==='special'?.42:attack==='heavy'?.2:attack==='throw'?.1:0
  return clamp01(base+attackBoost)
}
export function deriveRealityFrame(a:RealityActorSample,b:RealityActorSample,impactKind?:ImpactCueKind,impactAttack?:AttackKind|null):RealityFrame{
  const healthDanger=Math.max(1-a.health/Math.max(1,a.maxHealth),1-b.health/Math.max(1,b.maxHealth))
  const combo=clamp01(Math.max(a.comboCount,b.comboCount)/8)
  const active=a.attackPhase==='active'||b.attackPhase==='active'
  const startup=a.attackPhase==='startup'||b.attackPhase==='startup'
  const special=a.lastAttack==='special'||b.lastAttack==='special'
  const heavy=a.lastAttack==='heavy'||b.lastAttack==='heavy'
  const flash=clamp01(Math.max(a.attackFlash,b.attackFlash)*3.4)
  const impact=impactWeight(impactKind,impactAttack)
  const aerial=clamp01(Math.max(a.y,b.y)/3)
  const cinematic=clamp01((special?.55:heavy?.22:0)+(active?.22:startup?.09:0)+combo*.28+impact*.32+aerial*.12)
  const intensity=clamp01(.08+flash*.22+combo*.2+impact*.34+(active?.14:0)+healthDanger*.1+aerial*.08)
  const timeCompression=clamp01(
    impact*(impactAttack==='special'?.74:impactAttack==='heavy'?.42:.22)
    +(impactKind==='guard-break'?.18:impactKind==='parry'?.1:0)
    +(combo>.62?.12:0)
  )
  const worldResponse=clamp01(intensity*.5+impact*.42+combo*.16+(special&&active?.12:0))
  const materialStress=clamp01(healthDanger*.62+impact*.24+combo*.12+(heavy&&active?.06:0))
  const phase:RealityPhase=
    healthDanger>.9&&(impact>.6||active)?'collapse'
    :cinematic>.7||combo>.62||healthDanger>.76?'climax'
    :intensity>.34||healthDanger>.42?'pressure'
    :'neutral'
  return{
    intensity,
    danger:clamp01(healthDanger),
    cinematic,
    exposure:1.02+intensity*.08+impact*.1+(special&&active?.06:0),
    fogNearScale:1-intensity*.08-impact*.05-worldResponse*.025,
    fogFarScale:1+cinematic*.12+impact*.08+worldResponse*.04,
    particleEnergy:clamp01(intensity*.72+impact*.55+combo*.18),
    chromaImpulse:clamp01(impact*.72+(special&&active?.28:0)+timeCompression*.15),
    vignetteImpulse:clamp01(healthDanger*.48+cinematic*.22+materialStress*.08),
    timeCompression,
    worldResponse,
    materialStress,
    phase,
  }
}
